'use client';

import { useState, useEffect } from 'react';
import AdminLayout from '../AdminLayout';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon, Cog6ToothIcon, PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useBillingService, ProductDto, PriceDto } from '../api/billing';
import ManageProductModal from './ManageProductModal';
import ConfirmDialog from '@/components/common/ConfirmDialog';

const ProductsPricingTab = () => {
  const billingService = useBillingService();
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [prices, setPrices] = useState<PriceDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isManagingProduct, setIsManagingProduct] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductDto | null>(null);
  const [confirmDeleteProduct, setConfirmDeleteProduct] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [productsData, pricesData] = await Promise.all([
        billingService.getAllProducts(),
        billingService.getAllPrices(),
      ]);
      setProducts(productsData);
      setPrices(pricesData);
    } catch (err) {
      setError('Failed to load billing configuration.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleManageProduct = (product: ProductDto | null) => {
    setSelectedProduct(product);
    setIsManagingProduct(true);
  };

  const executeDeleteProduct = async (productId: string) => {
    try {
      await billingService.deleteProduct(productId);
      fetchData(); // Refetch data to update the UI
    } catch (err) {
      setError('Failed to delete product.');
      console.error(err);
    }
  };

  const handleDeleteProduct = (productId: string) => {
    setConfirmDeleteProduct(productId);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <span className="ml-3">Loading Products & Prices...</span>
      </div>
    );
  }

  if (error) {
    return <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md">{error}</div>;
  }

  return (
    <>
      <div className="bg-white shadow rounded-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800">Manage Products and Pricing</h2>
        <button
          onClick={() => handleManageProduct(null)} // `null` for creating a new product
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Product
        </button>
      </div>
      <div className="space-y-8">
        {products.map((product) => (
          <div key={product.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{product.name}</h3>
                <p className="text-sm text-gray-600 mt-1">{product.description}</p>
                <span
                  className={`mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    product.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}
                >
                  {product.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <button onClick={() => handleManageProduct(product)} className="text-blue-600 hover:text-blue-900 flex items-center">
                  <PencilIcon className="h-5 w-5 mr-1" /> Manage
                </button>
                <button onClick={() => handleDeleteProduct(product.id)} className="text-red-600 hover:text-red-900 flex items-center">
                  <TrashIcon className="h-5 w-5 mr-1" /> Delete
                </button>
              </div>
            </div>
            <div className="mt-4">
              <h4 className="font-semibold text-gray-700 mb-2">Prices:</h4>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interval</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {prices.filter(p => p.product_id === product.id).map(price => (
                    <tr key={price.id}>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 capitalize">{price.interval}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{formatCurrency(price.unit_amount)}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          price.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {price.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
      </div>

      {isManagingProduct && (
        <ManageProductModal
          product={selectedProduct}
          allPrices={prices}
          onClose={() => setIsManagingProduct(false)}
          onSave={() => {
            setIsManagingProduct(false);
            fetchData();
          }}
        />
      )}

      {confirmDeleteProduct && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setConfirmDeleteProduct(null)}
          onConfirm={() => executeDeleteProduct(confirmDeleteProduct)}
          title="Delete Product?"
          message="Are you sure you want to delete this product and all its prices?"
          confirmText="Delete Product"
        />
      )}
    </>
  );
};

export default function AdminSettingsPage() {
  const [activeTab, setActiveTab] = useState('products-pricing');

  const tabs = [
    { id: 'products-pricing', name: 'Products & Pricing' },
    // Future tabs can be added here
  ];

  return (
    <AdminLayout>
      {/* Breadcrumb Navigation */}
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link to="/admin-tools" className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600">
              <HomeIcon className="w-4 h-4 mr-2" />
              Admin Tools
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <ChevronRightIcon className="w-4 h-4 text-gray-400" />
              <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">System Settings</span>
            </div>
          </li>
        </ol>
      </nav>

      <div className="flex items-center mb-6">
        <Cog6ToothIcon className="h-8 w-8 text-gray-700 mr-3" />
        <h1 className="text-2xl font-bold text-gray-800">System Settings</h1>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'products-pricing' && <ProductsPricingTab />}
      </div>
    </AdminLayout>
  );
}
